import styled, {css} from 'styled-components'
import {themeGet, sx, SxProp} from '../..'

export type StyledBaseWrapperProps = {
  block?: boolean
  isDisabled?: boolean
  isInputFocused?: boolean
  hasTrailingAction?: boolean
  size?: TextInputSizes
} & SxProp

const autocompleteBasePadding = '12px'
const autocompleteBaseVerticalPadding = '3px'
export const textInputHorizontalPadding = autocompleteBasePadding

const renderFocusStyles = (hasTrailingAction: boolean, isInputFocused: boolean) => {
  if (hasTrailingAction) {
    return (
      isInputFocused &&
      css`
        border-color: ${themeGet('colors.border.focused')};
        outline: none;
        box-shadow: inset 0 0 0 1px ${themeGet('colors.border.focused')};
      `
    )
  }
  return css`
    &:focus-within {
      border-color: ${themeGet('colors.border.focused')};
      outline: none;
      box-shadow: inset 0 0 0 1px ${themeGet('colors.border.focused')};
    }
  `
}

export type TextInputSizes = 'small' | 'medium' | 'large'

// start with a base wrapper for low-level structural input styles
export const AutocompleteBaseWrapper = styled.span<StyledBaseWrapperProps>`
  font-size: ${themeGet('fontSizes.2')};
  line-height: 20px;
  color: ${themeGet('colors.text.default')};
  vertical-align: middle;
  background-color: ${themeGet('colors.background.input.default')};
  border: 1px solid ${themeGet('colors.border.default')};
  border-radius: ${themeGet('radii.2')};
  outline: none;
  display: inline-flex;
  align-items: stretch;
  min-height: 32px;
  input,
  textarea {
    cursor: text;
  }

  select {
    cursor: pointer;
  }

  &::placeholder {
    color: ${themeGet('colors.text.subtlest')};
  }

  ${p => renderFocusStyles(Boolean(p.hasTrailingAction), Boolean(p.isInputFocused))}

  > textarea {
    padding: ${autocompleteBasePadding};
  }

  // sizes logic here
  ${p =>
    p.isDisabled &&
    css`
      color: ${themeGet('colors.text.disabled')};
      background-color: ${themeGet('colors.background.disabled')};
      border-color: ${themeGet('colors.border.default')};

      input,
      textarea,
      select {
        cursor: not-allowed;
      }
    `}

  ${p =>
    p.block &&
    css`
      width: 100%;
      display: flex;
      align-self: stretch;
    `}

    // Ensures inputs do not zoom on mobile and are body-font size on desktop
  @media (min-width: ${themeGet('breakpoints.1')}) {
    font-size: ${themeGet('fontSizes.2')};
  }
  // base-level dynamic size props
  // @TODO: improve this api with an object lookup + CSS vars pattern
  --inner-action-size: ${p => (p.size === 'small' ? '20px' : p.size === 'large' ? '28px' : '24px')};
  padding-left: ${p =>
    p.size === 'small' ? themeGet('space.1') : p.size === 'large' ? themeGet('space.1') : 'revert'};
  padding-right: ${p =>
    p.size === 'small' ? themeGet('space.1') : p.size === 'large' ? themeGet('space.1') : 'revert'};
  padding-top: ${p => (p.size === 'small' ? '0px' : p.size === 'large' ? '10px' : autocompleteBaseVerticalPadding)};
  padding-bottom: ${p => (p.size === 'small' ? '0px' : p.size === 'large' ? '10px' : autocompleteBaseVerticalPadding)};
  min-height: ${p => (p.size === 'small' ? '28px' : 'revert')};
  font-size: ${p => (p.size === 'small' ? themeGet('fontSizes.1') : 'revert')};
  line-height: ${p => (p.size === 'small' ? '20px' : 'revert')};
  height: ${p => (p.size === 'large' ? '40px' : 'revert')};
  ${sx};
`

export type StyledWrapperProps = {
  hasLeadingVisual?: boolean
  hasTrailingVisual?: boolean
} & StyledBaseWrapperProps

const fontSize = () => 14
// compose autocompleteBaseWrapper for leading & trailing visual styles
const AutocompleteInputWrapper = styled(AutocompleteBaseWrapper)<StyledWrapperProps>`
  background-repeat: no-repeat; // Need repeat and position set for form states (success, error, etc.)
  background-position: right 8px center; // For form validation. Keeps images 8px from right and centered vertically.

  // add a margin to all elements in the input that are *not* a trailingVisual
  & > :not(:last-child) {
    margin-right: ${themeGet('space.2')};
  }

  .autocomplete-input-visual,
  .autocomplete-input-action {
    align-self: center;
    color: ${themeGet('colors.icon.subtle')};
    flex-shrink: 0;
  }

  ${p =>
    css`
      padding-left: ${p.hasLeadingVisual ? textInputHorizontalPadding : 0};
      padding-right: ${p.hasTrailingVisual && !p.hasTrailingAction ? textInputHorizontalPadding : 0};

      > input,
      > select {
        padding-left: ${!p.hasLeadingVisual ? 12 : 0};
        padding-right: ${!p.hasTrailingVisual && !p.hasTrailingAction ? textInputHorizontalPadding : 0};
      }
    `}

  vertical-align: middle;
  //
  line-height: ${(8 * 2.5) / fontSize()};
  //
  align-items: center;
  border-radius: ${themeGet('radii.1')};
  border-width: 2;
  border-style: solid;
  display: flex;
  flex: 1;
  width: 100%;
  font-size: ${themeGet('fontSizes.2')};
  justify-content: space-between;
  //
  overflow: hidden;
  transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
  pointer-events: auto;
  //
  padding: ${themeGet('space.2')} 12px;
  //
  cursor: text;
  background-color: ${themeGet('colors.background.input.default')};
  border-color: ${themeGet('colors.border.input')};
  color: inherit;
  //
  &[aria-invalid] {
    border-color: ${themeGet('colors.border.danger')};
  }

  &:focus {
    outline: none;
    border-color: ${themeGet('colors.border.focused')};
    box-shadow: 0 0 0 1px ${themeGet('colors.border.focused')};
    &[aria-invalid] {
      border-color: ${themeGet('colors.border.danger')};
      box-shadow: 0 0 0 1px ${themeGet('colors.border.danger')};
    }
  }
  ${sx}
`
export default AutocompleteInputWrapper
